import useChannel from "@/hooks/useChannel";
import { cn } from "@/libraries/cn";
import { genChannelName } from "@/libraries/helper";
import { getMemberQueryOptions } from "@/queries";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ModeEditOutlineRoundedIcon from "@mui/icons-material/ModeEditOutlineRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";

interface DrawerViewMemberProps extends React.ComponentPropsWithoutRef<"div"> {
	memberId: number;
	onClose: () => void;
}

const DrawerViewMember: React.FC<DrawerViewMemberProps> = ({
	memberId,
	onClose,
	className,
	...props
}) => {
	const navigate = useNavigate();

	const { mutateCreateChannel } = useChannel();

	const memberQuery = useQuery(getMemberQueryOptions(memberId));
	const member = memberQuery.data;

	const onClickChatMember = useCallback(
		async (e: React.MouseEvent<HTMLDivElement>) => {
			if (!member?.user) return;
			e.preventDefault();
			let channelName = member.user.channel_name;
			if (!channelName) {
				const resNewChannel = await mutateCreateChannel.mutateAsync({
					name: genChannelName("single"),
					messageable_id: member.user.id.toString(),
					messageable_type: "User",
				});
				channelName = String(resNewChannel.user.channel_name);
			}
			return navigate({
				to: "/dashboard/chat/single/$id/$channelName",
				params: { id: member.user.id, channelName },
			});
		},
		[member, mutateCreateChannel, navigate],
	);

	return (
		<div className={cn("relative flex flex-col", className)} {...props}>
			<div className="container py-4 flex flex-row justify-end">
				<IconButton color="primary" onClick={onClose}>
					<CloseRoundedIcon />
				</IconButton>
			</div>
			<Divider />
			<div className="container mb-4">
				<List disablePadding>
					<ListItem disablePadding>
						<ListItemButton
							disabled={!member?.user?.id}
							onClick={onClickChatMember}
						>
							<ListItemAvatar>
								<Avatar className="!bg-primary-light">
									<ModeEditOutlineRoundedIcon className="!text-primary" />
								</Avatar>
							</ListItemAvatar>
							<ListItemText primary={`Chat ${member?.user?.fullname}`} />
						</ListItemButton>
					</ListItem>
					<Divider component="li" />
					<ListItem disablePadding>
						<ListItemButton
							disabled={!member?.user?.id}
							onClick={() =>
								navigate({
									to: "/dashboard/contacts/member/$id",
									params: { id: member?.user?.id },
								})
							}
						>
							<ListItemAvatar>
								<Avatar className="!bg-primary-light">
									<PersonAddAltRoundedIcon className="!text-primary" />
								</Avatar>
							</ListItemAvatar>
							<ListItemText primary={`View ${member?.user?.fullname}`} />
						</ListItemButton>
					</ListItem>
				</List>
			</div>
		</div>
	);
};

export default DrawerViewMember;
